import { Box, Slider, styled, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { asCm, formatDistanceCm } from '~/utils/measurementFormat';
import { DvfDisplayRangeMm } from '~/domain/dvf/dvfDisplayRange';
import { rampGradientCss } from './dvfGlyphs';

const STEP_MM = 0.1;

const GradientSlider = styled(Slider)(({ theme }) => ({
    height: 8,
    padding: '8px 0',
    '& .MuiSlider-rail': {
        height: 8,
        opacity: 1,
        borderRadius: 4,
    },
    '& .MuiSlider-track': {
        height: 8,
        background: 'transparent',
        border: `2px solid ${theme.palette.background.paper}`,
        borderRadius: 4,
    },
    '& .MuiSlider-thumb': {
        width: 8,
        height: 14,
        borderRadius: 1,
        border: '1px solid #fff',
        backgroundColor: theme.palette.primary.main,
        '&::after': {
            // MUI's 42px default hit area would overlap the thumbs and swallow rail clicks.
            width: 20,
            height: 20,
        },
    },
}));

interface DvfRangeSliderProps {
    maxMm: number;
    range: DvfDisplayRangeMm;
    onChange: (range: DvfDisplayRangeMm) => void;
}

export default function DvfRangeSlider({
    maxMm,
    range,
    onChange,
}: DvfRangeSliderProps) {
    const intl = useIntl();
    const adjustable = maxMm > STEP_MM;

    // The handles never cross or meet: the moving one pushes the other ahead.
    const handleChange = (
        _e: Event,
        value: number | number[],
        activeThumb: number
    ) => {
        const [lowMm, highMm] = value as number[];
        if (highMm - lowMm >= STEP_MM) {
            onChange({ lowMm, highMm });
        } else if (activeThumb === 0) {
            const low = Math.min(lowMm, maxMm - STEP_MM);
            onChange({ lowMm: low, highMm: low + STEP_MM });
        } else {
            const high = Math.max(highMm, STEP_MM);
            onChange({ lowMm: high - STEP_MM, highMm: high });
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption">0</Typography>
                <Box sx={{ flex: 1, minWidth: 64, display: 'flex' }}>
                    {adjustable ? (
                        <GradientSlider
                            size="small"
                            value={[range.lowMm, range.highMm]}
                            min={0}
                            max={maxMm}
                            step={STEP_MM}
                            disableSwap
                            onChange={handleChange}
                            getAriaLabel={(index) =>
                                intl.formatMessage({
                                    id:
                                        index === 0
                                            ? 'toolbar.deformationField.displayRangeFrom'
                                            : 'toolbar.deformationField.displayRangeTo',
                                })
                            }
                            getAriaValueText={formatDistanceCm}
                            sx={{
                                width: '100%',
                                '& .MuiSlider-rail': {
                                    background: rampGradientCss(range, maxMm),
                                },
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: 8,
                                borderRadius: 1,
                                background: rampGradientCss(range, maxMm),
                            }}
                        />
                    )}
                </Box>
                <Typography variant="caption">
                    {formatDistanceCm(maxMm)}
                </Typography>
            </Box>
            {adjustable && (
                <Typography
                    variant="caption"
                    color="primary.main"
                    textAlign="right"
                >
                    <FormattedMessage
                        id="toolbar.deformationField.displayRangeSpan"
                        values={{
                            low: asCm(range.lowMm),
                            high: asCm(range.highMm),
                        }}
                    />
                </Typography>
            )}
        </Box>
    );
}
